import { Injectable } from '@angular/core';
import { UtilDocument, UtilDocumentId } from '@formbird/shared';
import { IApplicationState } from '../../redux/state/application.state';
import {
  formResetDocumentInfo,
  formResetToOrgDocument,
  formSetDocument,
  formSetDocumentInfo,
  formSetupDocumentList
} from '../../redux/actions';
import { ClientConstants } from '../../constants/ClientConstants';
import { DocumentInfo } from '@formbird/types';
import { AppStore } from '../../redux/store/app.store';

const logger = console;

@Injectable()
export class UnsavedDocumentService {

  constructor(
    private appStore: AppStore<IApplicationState>
  ) {
  }

  /**
   * create a new unsaved document list. The unsaved document list is a list of documents that will be saved in a single
   * operation. This allows saving of documents independently of the nav bar save button. Eg. If you open up a dialog with a
   * template with a child doc then you need to save several documents on dialog close without saving the main document loaded
   * in the form. We can't put parameters in the save function to handle special cases like whether the save is for a dialog
   * because we would have to keep adding more special cases for saving from different locations.
   */
  createUnsavedDocumentList(name, existingUnsavedDocumentListId?) {

    // generate an unsaved document list id, this will be used by the caller to keep track of this list. It will be passed
    // to the save function to trigger the save of the documents in that list
    const unsavedDocumentListId = existingUnsavedDocumentListId ? existingUnsavedDocumentListId : UtilDocumentId.generateId();
    const isMainDoc = name === ClientConstants.MAIN_DOCUMENT_INFO_LIST;

    this.appStore.dispatch(formSetupDocumentList(unsavedDocumentListId, isMainDoc));

    return unsavedDocumentListId;
  }

  /**
   * add a document to the list, so it will be included in the list of unsaved documents
   * @param document the document
   * @param unsavedDocumentListId the unsavedDocumentListId
   */
  addDocumentToUnsavedList(document, unsavedDocumentListId) {

    if (!document || !document.documentId) {
      throw new Error('No document or documentId provided in addDocumentToUnsavedList');

    } else if (!unsavedDocumentListId) {
      throw new Error('No unsavedDocumentListId provided in addDocumentToUnsavedList');

    } else {

      const docInfo: DocumentInfo = {
        documentId: document.documentId
      };

      this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));

      this.appStore.dispatch(formSetDocument(document));

      logger.info('Added document ' + document.documentId + ' to unsaved list ' + unsavedDocumentListId);

    }
  }

  /**
   * add a template to the list
   * @param template the template obj
   * @param unsavedDocumentListId the unsaved list
   * @param orgTemplateId the original template id
   */
  addTemplateToUnsavedList(template, unsavedDocumentListId, orgTemplateId) {

    if (!template || !template.documentId) {
      throw new Error('No template provided in addTemplateToUnsavedList');

    } else if (!unsavedDocumentListId) {
      throw new Error('No unsavedDocumentListId provided in addTemplateToUnsavedList');

    } else {

      const docInfo: DocumentInfo = {};

      const templateId = template.documentId;

      // in case alt template
      if (orgTemplateId && templateId !== orgTemplateId) {

        docInfo.templateId = orgTemplateId;
        docInfo.altTemplateId = templateId;

      } else {

        docInfo.templateId = templateId;
      }

      this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));

      logger.info('Added template ' + templateId + ' to unsaved list ' + unsavedDocumentListId);

    }
  }

  /**
   * get the document from the unsaved document list. The document will be set in the document parameter rather than returned
   * so that the document directly references the value in the unsaved list
   * @param documentResult - the document will be placed in the document parameter of documentResult. It needs to be done this
   *                         way because if you assign to an object directly, the reference that the object points at changes
   * @param unsavedDocumentListId the unsaved list Id
   * @param documentId the document Id
   */
  getDocument(documentResult, unsavedDocumentListId, documentId) {

    const formState = this.appStore.getState().formState;
    const documents = formState.documents;
    const docInfo: DocumentInfo = formState.documentInfo;

    if (!documentResult) {
      throw new Error('The documentResult must be passed as an object to getDocument so the document can be set under it');

    } else if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not provided in getDocument');

    } else if (!docInfo[unsavedDocumentListId]) {
      throw new Error('GetDocument: Unsaved document list has not been initialised with id: ' + unsavedDocumentListId);

    } else if (!docInfo[unsavedDocumentListId][documentId]) {
      throw new Error('Unsaved document list does not contain document: ' + documentId);
    }

    // set the document from loaded docs in the document parameter
    documentResult.document = documents[documentId];
  }

  /**
   * get the template from the unsaved document list. The template will be set in the template result parameter
   * rather than returned so that the template directly references the value in the unsaved list
   * @param documentResult - the template will be placed in the template parameter of documentResult. It needs to
   * be done this way because if you assign to an object directly, the reference that the object points at changes
   * @param unsavedDocumentListId the unsaved list Id
   * @param templateId the template Id
   */
  getTemplate(documentResult, unsavedDocumentListId, templateId) {

    const formState = this.appStore.getState().formState;
    const documents = formState.documents;
    const docInfo: DocumentInfo = formState.documentInfo;

    if (!documentResult) {
      throw new Error('The documentResult must be passed as an object to getTemplate so the template can be set under it');

    } else if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not provided in getTemplate');

    } else if (!docInfo[unsavedDocumentListId]) {
      throw new Error('GetTemplate: Unsaved document list has not been initialised with id: ' + unsavedDocumentListId);

    }

    if (docInfo[unsavedDocumentListId].templateId === templateId || docInfo[unsavedDocumentListId].altTemplateId === templateId) {

      // set the template from loaded templates in the document parameter
      documentResult.template = documents[templateId];
    }

    if (!templateId) {
      throw new Error('Unsaved document list does not contain template: ' + templateId);
    }

  }

  setDocument(unsavedDocumentListId, document) {

    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not provided in setDocument');

    }

    const docInfo: DocumentInfo = {
      documentId: document.documentId
    };

    this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
    this.appStore.dispatch(formSetDocument(document));
  }


  setTemplate(unsavedDocumentListId, documentId, template) {

    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not provided in setTemplate');

    } else if (!template || !template.documentId) {
      throw new Error('Template not provided in setTemplate');

    }

    const docInfo: DocumentInfo = {
      templateId: template.documentId
    };

    this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
    this.appStore.dispatch(formSetDocument(template));
  }

  /**
   * get the document from a unsaved document list.
   * @param documentId the document Id
   */
  getDocumentOnly(documentId) {

    return this.appStore.getState().formState.documents[documentId];
  }

  /**
   * get the template from the unsaved document list.
   * @param templateId the template Id
   */
  getTemplateOnly(templateId) {

    return this.getDocumentOnly(templateId);
  }

  setDocumentListOptions(unsavedDocumentListId, documentId, key, value) {

    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not provided in getDocumentOptions');

    }

    const docInfo: DocumentInfo = {
      options: {
        [key]: value
      }
    };

    this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
  }


  getDocumentListOptions(unsavedDocumentListId) {

    const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;

    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not provided in getDocumentOptions');

    } else if (!docInfo[unsavedDocumentListId] || !docInfo[unsavedDocumentListId].options) {
      throw new Error('getDocumentOptions: Unsaved document list has not been initialised with id: ' + unsavedDocumentListId);
    }

    return docInfo[unsavedDocumentListId].options;
  }

  getDocumentOptionKey(unsavedDocumentListId, documentId, optionKey) {

    const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;

    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not provided in getDocumentOptions');

    } else if (!docInfo[unsavedDocumentListId] || !docInfo[unsavedDocumentListId].options) {
      throw new Error('getDocumentOptionKey: Unsaved document list has not been initialised with id: ' + unsavedDocumentListId);
    }

    return docInfo[unsavedDocumentListId].options[optionKey];
  }

  /**
   * get the template from a unsaved document list.
   * @param templateId the template id
   * @param documentId the document id
   */
  getTemplateWithNoUnSaveList(templateId, documentId) {

    if (templateId && documentId) {

      const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;
      const documents = this.appStore.getState().formState.documents;

      const unsavedDocumentListIds = Object.keys(docInfo);

      for (let i = 0; i < unsavedDocumentListIds.length; i++) {

        const unsavedDocumentListId = unsavedDocumentListIds[i];

        if (docInfo[unsavedDocumentListId].documentId === documentId) {

          if (docInfo[unsavedDocumentListId].templateId === templateId || docInfo[unsavedDocumentListId].altTemplateId === templateId) {
            const template = documents[templateId];
            if (template) {

              return template;
            }
          }
        }

      }

    }
  }

  /**
   * get the unsaved documents for the unsaved document list
   * @param unsavedDocumentListId - the id of the list to get the loaded docs for
   */
  getUnsavedDocuments(unsavedDocumentListId) {

    const documents = this.appStore.getState().formState.documents;
    const documentId = this.getUnsavedDocumentId(unsavedDocumentListId);
    return [documents[documentId]];
  }

  getUnsavedDocumentId(unsavedDocumentListId) {
    const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;

    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not specified when getting loaded documents');

    } else if (!docInfo[unsavedDocumentListId]) {
      throw new Error('Unsaved documents not found with id ' + unsavedDocumentListId);
    }

    return docInfo[unsavedDocumentListId].documentId;
  }

  /**
   * get the unsaved templates for the unsaved document list
   * @param unsavedDocumentListId - the id of the list to get the loaded templates for
   */
  getUnsavedTemplates(unsavedDocumentListId) {

    const documents = this.appStore.getState().formState.documents;
    const templateId = this.getUnsavedTemplateId(unsavedDocumentListId);
    return [documents[templateId]];
  }

  getUnsavedTemplateId(unsavedDocumentListId) {
    const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;

    if (!unsavedDocumentListId) {
      throw new Error('Unsaved document list id not specified when getting loaded templates');

    } else if (!docInfo[unsavedDocumentListId]) {
      throw new Error('Unsaved documents not found with id ' + unsavedDocumentListId);
    }

    return docInfo[unsavedDocumentListId].templateId;
  }

  /**
   * remove a document from a list. This will be needed when a document is no longer to be part of the list, eg. when
   * cancelling the save of a particular document
   * @param unsavedDocumentListId the unsaved list Id
   * @param documentId the document Id
   */
  removeDocumentFromList(unsavedDocumentListId, documentId) {

    // if (!unsavedDocumentListId) {
    //   throw new Error('Unsaved document list id not provided in getDocument');
    //
    // } else if (!this.documents[unsavedDocumentListId] || !this.documents[unsavedDocumentListId].loadedDocs) {
    //   throw new Error('RemoveDocumentFromList: Unsaved document list has not been initialised with id: ' + unsavedDocumentListId);
    //
    // } else if (!this.documents[unsavedDocumentListId].loadedDocs[documentId]) {
    //   throw new Error('Unsaved document list does not contain document: ' + documentId);
    // }
    //
    // delete this.documents[unsavedDocumentListId].loadedDocs[documentId];
    //
    // this.documents[unsavedDocumentListId].loadedDocs = {};
  }

  removeDocumentList(unsavedDocumentListId) {

    this.appStore.dispatch(formResetDocumentInfo(unsavedDocumentListId));
  }

  getDocumentListByType(type) {

    const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;

    const unsavedDocumentListIds = Object.keys(docInfo);

    const documentLists = [];

    for (let i = 0; i < unsavedDocumentListIds.length; i++) {

      const unsavedDocumentListId = unsavedDocumentListIds[i];

      if (docInfo[unsavedDocumentListId].name === type) {

        documentLists.push(docInfo[unsavedDocumentListId]);
      }
    }

    return documentLists;
  }

  /**
   * store a document hierarchy info to a list. This will be needed when retrieve information to load a child
   * document from a parent one
   * @param hierarchyInfo (parentId and childId)
   * @param unsavedDocumentListId the unsaved list Id
   */
  setHierarchyInfoToUnsavedList(hierarchyInfo, unsavedDocumentListId) {

    const docInfo: DocumentInfo = {};

    docInfo.hierarchyInfo = hierarchyInfo;

    this.appStore.dispatch(formSetDocumentInfo(unsavedDocumentListId, docInfo));
  }

  clearDocuments() {
    // this.documents = {};
  }

  getAllLoadedDocs(excludedOptions?) {

    const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;
    const documents = this.appStore.getState().formState.documents;

    const loadedDocs = [];

    const unsavedDocumentListIds = Object.keys(docInfo);

    unsavedDocumentListIds.forEach(function(unsavedDocumentListId) {

      const options = docInfo[unsavedDocumentListId].options;
      const docIds = Object.keys(documents);

      docIds.forEach(function(docId) {

        const doc = documents[docId];
        if (excludedOptions) {
          if (!UtilDocument.hasIntersect(options[docId], excludedOptions)) {
            loadedDocs.push(doc);
          }
        } else {
          loadedDocs.push(doc);
        }

      });
    });

    return loadedDocs;
  }

  documentIsLoaded(documentId) {

    const docInfo: DocumentInfo = this.appStore.getState().formState.documentInfo;
    const documents = this.appStore.getState().formState.documents;

    let result = false;

    const unsavedDocumentListIds = Object.keys(docInfo);

    unsavedDocumentListIds.forEach(function(unsavedDocumentListId) {

      if (documents[documentId] && docInfo[unsavedDocumentListId].documentId === documentId) {

        result = true;
      }
    });

    return result;
  }

  isDocumentsDefined(unsavedDocumentListId) {
    return !!this.appStore.getState().formState.documentInfo[unsavedDocumentListId];
  }

  getDocuments() {
    return this.appStore.getState().formState.documentInfo;
  }

  resetToOrgDocument(documentId) {
    this.appStore.dispatch(formResetToOrgDocument(documentId));
  }
}
